/* eslint-disable max-lines-per-function */
import { useQuery } from '@apollo/client';
import { makeDataErrorMessages } from '@atlassian/amkt-common';
import { ANALYTICS_EVENT_CONTAINER_ONE_CLICK_INSTALL_DIALOG, ANALYTICS_SUBJECT_ID_SELECT_SITE, DropdownOptionSelectedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { getScopesLookup, getSiteDetails } from '@atlassian/mpac-api-client';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { DEFAULT_CURRENCY_CODE } from '@atlassian/mpac-pricing-utils';
import { useBillingSystem, useSelectedSiteAndProductDetails } from '@atlassian/mpac-primitives';
import { AppPricing, GetCmtAvailability, GetHostStatus, GetInstalledAppDetails, StargateAPI } from '@atlassian/mpac-services-client';
import { AtlassianProductHostingType } from '@atlassian/mpac-types';
import { MarketplaceStoreBillingSystem, MarketplaceStoreInstallationTargetProduct } from '@atlassian/mpac-types/graphql-gateway-types';
import { hooks } from '@atlassian/mpac-utils';
import { Either } from 'funfix-core';
import { useEffect, useState } from 'react';
export const useAvailableSites = (stargateBaseUrl, products) => {
  const availableSitesPromise = () => new StargateAPI(stargateBaseUrl).marketplace.availableSites(products);
  const {
    result,
    error,
    pending
  } = hooks.usePromise(availableSitesPromise, [products, stargateBaseUrl]);
  return {
    availableSitesPending: pending,
    availableSitesError: error,
    availableSitesResult: result === null || result === void 0 ? void 0 : result.sites
  };
};
export const useSelectedSiteDetails = (mpacBaseUrl, selectedSiteCloudId) => {
  const {
    enableDemandServiceSiteDetailsDomainChange
  } = useStatsig();
  const selectedSiteDetailsPromise = () => getSiteDetails(mpacBaseUrl, selectedSiteCloudId, enableDemandServiceSiteDetailsDomainChange());
  const {
    result,
    error,
    pending
  } = hooks.usePromise(selectedSiteDetailsPromise, [mpacBaseUrl, selectedSiteCloudId]);
  return {
    selectedSiteDetailsPending: pending,
    selectedSiteDetailsError: error,
    selectedSiteDetailsResult: result
  };
};
export const useAppInstallPermission = (stargateBaseUrl, product, cloudId) => {
  const appInstallPermissionPromise = () => product && cloudId ? new StargateAPI(stargateBaseUrl).marketplace.appInstallPermission(product, cloudId) : new Promise(() => {
    /* empty promise, never resolves */
  });
  const {
    result,
    error,
    pending
  } = hooks.usePromise(appInstallPermissionPromise, [product, cloudId], !!(product && cloudId));
  return {
    appInstallPermissionPending: pending,
    appInstallPermissionError: error,
    appInstallPermissionResult: result
  };
};
export const ProductNameToTargetProductMap = {
  confluence: MarketplaceStoreInstallationTargetProduct.Confluence,
  jira: MarketplaceStoreInstallationTargetProduct.Jira,
  compass: MarketplaceStoreInstallationTargetProduct.Compass
};
// since we are skipping query if site or product is not selected, it is safe to assume that
// site.cloudId and product will always be defined
export const createInstallAppTargetInput = selectedSiteAndProduct => {
  return {
    cloudId: selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId,
    product: ProductNameToTargetProductMap[selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.product]
  };
};
export const useOneClickInstallSiteSelector = function useOneClickInstallSiteSelector(appKey, appCompatibilities, stargateBasePath, showAppPlans, onChange) {
  let skipInstalledAppManageLink = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : true;
  let enableLdCustomerGaChanges = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  const fireAnalyticsEvent = useAnalyticsEvents();
  const [selectedSiteAndProduct, setSelectedSiteAndProduct] = useState();
  const [selectedSiteAndProductDetails, setSelectedSiteAndProductDetails] = useState();
  const selectedSiteAndProductDetailsPromise = useSelectedSiteAndProductDetails({
    input: {
      cloudId: (selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId) || '',
      product: {
        appKey,
        parentProduct: (selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.product) && ProductNameToTargetProductMap[selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.product]
      }
    },
    skip: !enableLdCustomerGaChanges || !selectedSiteAndProduct
  });
  const billingSystemPromise = useBillingSystem({
    billingSystemInput: {
      cloudId: (selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId) || ''
    },
    showAppPlans
  });
  const availableSitesPromise = useAvailableSites(stargateBasePath, appCompatibilities);
  const appInstallPermissionPromise = useAppInstallPermission(stargateBasePath, selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.product, selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId);
  const installAppTargetInput = createInstallAppTargetInput(selectedSiteAndProduct);
  const skipQuery = !selectedSiteAndProduct || !selectedSiteAndProduct.product || !selectedSiteAndProduct.site.cloudId;
  const {
    data: installedAppDetailsResult,
    loading: installedAppDetailsLoading,
    error: installedAppDetailsError
  } = useQuery(GetInstalledAppDetails, {
    variables: {
      input: {
        appKey,
        target: installAppTargetInput
      },
      skipInstalledAppManageLink
    },
    skip: skipQuery
  });
  const {
    data: hostStatusResult,
    loading: hostStatusLoading,
    error: hostStatusError
  } = useQuery(GetHostStatus, {
    variables: {
      input: installAppTargetInput
    },
    skip: skipQuery
  });
  const handleSiteChange = siteProductSelected => {
    if (Object.keys(siteProductSelected).length === 0) {
      setSelectedSiteAndProduct(undefined);
      setSelectedSiteAndProductDetails(undefined);
      return;
    }
    fireAnalyticsEvent({
      eventType: DropdownOptionSelectedEvent,
      source: ANALYTICS_EVENT_CONTAINER_ONE_CLICK_INSTALL_DIALOG,
      actionSubjectId: ANALYTICS_SUBJECT_ID_SELECT_SITE,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_ONE_CLICK_INSTALL_DIALOG,
        source: ANALYTICS_EVENT_CONTAINER_ONE_CLICK_INSTALL_DIALOG,
        site: siteProductSelected.site.cloudId,
        product: siteProductSelected.product
      }
    });
    setSelectedSiteAndProduct(siteProductSelected);
    if (onChange) {
      onChange(siteProductSelected);
    }
  };
  useEffect(() => {
    if (selectedSiteAndProductDetailsPromise) {
      setSelectedSiteAndProductDetails(selectedSiteAndProductDetailsPromise);
    }
  }, [selectedSiteAndProductDetailsPromise === null || selectedSiteAndProductDetailsPromise === void 0 ? void 0 : selectedSiteAndProductDetailsPromise.selectedSiteAndProductDetailsResult]);
  return {
    selectedSiteAndProduct,
    selectedSiteAndProductDetails,
    availableSitesPromise,
    appInstallPermissionPromise,
    installedAppDetailsPromise: {
      installedAppDetailsResult,
      installedAppDetailsLoading,
      installedAppDetailsError
    },
    hostStatusPromise: {
      hostStatusResult,
      hostStatusLoading,
      hostStatusError
    },
    billingSystemPromise,
    onSiteChange: handleSiteChange
  };
};
export const useScopesLookup = graphQLGatewayUrl => {
  var _error$message;
  const scopesLookupPromise = () => getScopesLookup(graphQLGatewayUrl);
  const {
    result,
    error,
    pending
  } = hooks.usePromise(scopesLookupPromise, [graphQLGatewayUrl]);
  return {
    pending,
    scopesLookup: result ? Either.right(result) : Either.left(makeDataErrorMessages([(_error$message = error === null || error === void 0 ? void 0 : error.message) !== null && _error$message !== void 0 ? _error$message : 'Failed to fetch scope lookups']))
  };
};
export const usePricingPlan = (appId, billingCurrency) => {
  const {
    data: pricingPlanResult,
    error: pricingPlanError,
    loading: pricingPlanPending
  } = useQuery(AppPricing, {
    variables: {
      appId,
      hosting: AtlassianProductHostingType.Cloud,
      currencyCode: billingCurrency !== null && billingCurrency !== void 0 ? billingCurrency : DEFAULT_CURRENCY_CODE
    }
  });
  return {
    pricingPlanResult,
    pricingPlanError,
    pricingPlanPending
  };
};
export const useGetCmtAvailability = (appKey, billingSystem, hostCmtEnabled, selectedSiteAndProduct) => {
  const skipQuery = !selectedSiteAndProduct || !selectedSiteAndProduct.product || !selectedSiteAndProduct.site.cloudId || !hostCmtEnabled || billingSystem !== MarketplaceStoreBillingSystem.Hams;
  const {
    data: cmtAvailabilityResult,
    error: cmtAvailabilityError,
    loading: cmtAvailabilityPending
  } = useQuery(GetCmtAvailability, {
    variables: {
      input: {
        appKey,
        target: createInstallAppTargetInput(selectedSiteAndProduct)
      }
    },
    skip: skipQuery
  });
  return {
    cmtAvailabilityResult,
    cmtAvailabilityError,
    cmtAvailabilityPending
  };
};